import {
  format,
  getMonth,
  isBefore,
  isAfter,
  startOfMonth,
  endOfMonth,
  isPast,
  endOfDay,
  startOfDay,
  isSameMonth,
  isSameYear
} from 'date-fns';

import {
  CONTRACT_TYPE,
  AVAILABILITY_TYPE,
  HALF_QUARTER_TYPE,
  HALF_QUARTER_LAST_TYPE
} from '@spa/shared/constants/availability';
import { formatMoney } from '@spa/shared/utils/money';

export const getQuartersOrHalfs = (date, isLast = false) => {
  const days = date.getDate();
  const start = startOfMonth(date).getDate();
  const end = endOfMonth(date).getDate();

  if (days === start || days === end) {
    return null;
  }

  const half = Math.floor(end / 2);
  const quarter = Math.floor(end / 3);
  const order = [quarter, half, quarter * 2];

  const closest = order.reduce(
    ({ pos, prev }, curr, index) =>
      Math.abs(curr - days) < Math.abs(prev - days) ? { pos: index, prev: curr } : { pos, prev },
    { pos: 0, prev: 0 }
  );

  return isLast ? HALF_QUARTER_LAST_TYPE[closest.pos] : HALF_QUARTER_TYPE[closest.pos];
};

export const getMonthClassName = (date, blockedIntervals = []) => {
  if (isPast(endOfMonth(date))) {
    return { className: '', childClassName: '' };
  }

  const today = new Date();
  const isCurrentMonth = format(today, 'yyyy-MM') === format(date, 'yyyy-MM');
  const currentMonthClass = isCurrentMonth ? getQuartersOrHalfs(today, true) : null;

  const blocked = blockedIntervals.reduce((acc, { start_date, end_date }) => {
    const startTemp = startOfDay(new Date(start_date));
    const end = endOfDay(new Date(end_date));
    const start = isAfter(end, today) && isBefore(startTemp, today) ? today : startTemp;

    if (isBefore(end, today)) {
      return acc;
    }

    const startMonth = endOfDay(startOfMonth(date));
    const endMonth = startOfDay(endOfMonth(date));

    const isMonthBlocked = isAfter(startMonth, start) && isBefore(endMonth, end);
    if (isMonthBlocked) {
      return {
        className: currentMonthClass ? '' : AVAILABILITY_TYPE.STANDARD_UNITARY.OCCUPIED,
        childClassName: `${currentMonthClass || ''} occupied`
      };
    }

    if (
      !(isSameYear(startMonth, start) && isSameMonth(startMonth, start)) &&
      !(isSameYear(endMonth, end) && isSameMonth(endMonth, end))
    ) {
      return acc;
    }

    const isStartAfter = isAfter(start, startMonth);
    const block = isStartAfter ? start : end;
    const partBlocked = getQuartersOrHalfs(block, isStartAfter);

    return {
      className: currentMonthClass ? '' : AVAILABILITY_TYPE.STANDARD_UNITARY.AVAILABLE,
      childClassName: `${partBlocked} occupied`
    };
  }, null);

  return (
    blocked || {
      className: currentMonthClass ? null : AVAILABILITY_TYPE.STANDARD_UNITARY.AVAILABLE,
      childClassName: currentMonthClass
    }
  );
};

export const getMonthPrice = (date, accommodationOffer, t) => {
  const isTypeFixed = [CONTRACT_TYPE.FIXED, CONTRACT_TYPE.FIXED_UNITARY].includes(accommodationOffer.contract.type);

  if (isTypeFixed) {
    const { amount, currency_code: currencyCode } = accommodationOffer.reference_price;
    return formatMoney(amount, currencyCode);
  }

  const rents = accommodationOffer.contract.standard?.rents || [];
  const monthIndex = getMonth(date);
  const rent = rents[monthIndex];

  if (!rent) {
    return t('no_price');
  }

  return formatMoney(rent.amount, rent.currency_code);
};
